
import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Helper to convert hex to HSL for Tailwind
// Returns "H S% L%" format
const hexToHSL = (hex) => {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    r /= 255;
    g /= 255;
    b /= 255;
    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) {
        h = s = 0; // achromatic
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    return `${h} ${s}% ${l}%`;
};

const contrastingForeground = (hex) => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!match) return '210 40% 98%';
    const channels = [match[1], match[2], match[3]].map(value => parseInt(value, 16) / 255);
    const linear = channels.map(value => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
    const luminance = (0.2126 * linear[0]) + (0.7152 * linear[1]) + (0.0722 * linear[2]);
    return luminance > 0.5 ? '222.2 47.4% 11.2%' : '210 40% 98%';
};

// Default theme values
const defaultTheme = {
    primary: '#0f172a',
    secondary: '#f1f5f9',
    accent: '#f1f5f9',
    fontFamily: 'Inter',
    fontSize: '16px'
};

const defaultInstitutionSettings = {
    timetable_day_starts_at: '07:00',
    timetable_day_ends_at: '20:00',
    timetable_break_starts_at: '',
    timetable_break_ends_at: '',
    timetable_working_days: [1, 2, 3, 4, 5]
};

const normalizeInstitutionSettings = (settings = {}) => {
    let workingDays = settings.timetable_working_days;
    if (typeof workingDays === 'string') {
        try { workingDays = JSON.parse(workingDays); } catch (_) { workingDays = null; }
    }
    const validDays = Array.isArray(workingDays)
        ? [...new Set(workingDays.map(Number).filter(day => day >= 1 && day <= 7))].sort((a, b) => a - b)
        : defaultInstitutionSettings.timetable_working_days;

    return {
        ...defaultInstitutionSettings,
        ...settings,
        timetable_working_days: validDays.length ? validDays : defaultInstitutionSettings.timetable_working_days
    };
};

const GOOGLE_FONT_FAMILIES = {
    Inter: 'Inter:wght@400;500;600;700',
    Roboto: 'Roboto:wght@400;500;600;700',
    'Open Sans': 'Open+Sans:wght@400;500;600;700',
    Lato: 'Lato:wght@400;700',
    Montserrat: 'Montserrat:wght@400;500;600;700',
    Poppins: 'Poppins:wght@400;500;600;700'
};

export const ThemeProvider = ({ children }) => {
    // Load from local storage or use default
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('app-theme');
        return savedTheme ? { ...defaultTheme, ...JSON.parse(savedTheme) } : defaultTheme;
    });
    const [institutionSettings, setInstitutionSettings] = useState(() => {
        try {
            const savedSettings = localStorage.getItem('institution-settings');
            return normalizeInstitutionSettings(savedSettings ? JSON.parse(savedSettings) : {});
        } catch (_) {
            return defaultInstitutionSettings;
        }
    });

    const updateInstitutionSettings = (updates) => {
        setInstitutionSettings(previous => {
            const next = normalizeInstitutionSettings({ ...previous, ...updates });
            localStorage.setItem('institution-settings', JSON.stringify(next));
            return next;
        });
    };

    const refreshInstitutionSettings = async () => {
        const { systemService } = await import('../services/systemService');
        const data = await systemService.getInstitutionDetails();
        if (!data) return null;

        updateInstitutionSettings(data);
        setTheme(previous => ({
            ...previous,
            primary: data.theme_primary || previous.primary,
            secondary: data.theme_secondary || previous.secondary,
            accent: data.theme_accent || previous.accent,
            fontFamily: data.theme_font_family || previous.fontFamily,
            fontSize: data.theme_font_size || previous.fontSize
        }));
        return data;
    };

    useEffect(() => {
        refreshInstitutionSettings().catch(error => console.warn('Unable to load institution settings:', error));
    }, []);

    useEffect(() => {
        const handleSettingsStorage = (event) => {
            if (event.key !== 'institution-settings' || !event.newValue) return;
            try { setInstitutionSettings(normalizeInstitutionSettings(JSON.parse(event.newValue))); } catch (_) {}
        };
        window.addEventListener('storage', handleSettingsStorage);
        return () => window.removeEventListener('storage', handleSettingsStorage);
    }, []);

    useEffect(() => {
        const root = document.documentElement;

        // Apply colors
        const primaryHSL = hexToHSL(theme.primary);
        if (primaryHSL) {
            root.style.setProperty('--primary', primaryHSL);
            root.style.setProperty('--primary-foreground', contrastingForeground(theme.primary));
        }

        const secondaryHSL = hexToHSL(theme.secondary);
        if (secondaryHSL) {
            root.style.setProperty('--secondary', secondaryHSL);
            root.style.setProperty('--secondary-foreground', contrastingForeground(theme.secondary));
        }

        const accentHSL = hexToHSL(theme.accent);
        if (accentHSL) {
            root.style.setProperty('--accent', accentHSL);
            root.style.setProperty('--accent-foreground', contrastingForeground(theme.accent));
        }

        // Apply typography at the document root. Components use rem-based type
        // scales, so changing the root size keeps the whole interface in step.
        const fontFamily = theme.fontFamily.includes(',')
            ? theme.fontFamily
            : `"${theme.fontFamily}", sans-serif`;
        root.style.setProperty('--app-font-family', fontFamily);
        root.style.setProperty('--app-font-size', theme.fontSize);
        root.style.setProperty('--font-sans', fontFamily);
        root.style.fontFamily = fontFamily;
        root.style.fontSize = theme.fontSize;

        const googleFont = GOOGLE_FONT_FAMILIES[theme.fontFamily];
        const fontLinkId = 'institution-theme-font';
        const existingFontLink = document.getElementById(fontLinkId);
        if (googleFont) {
            const href = `https://fonts.googleapis.com/css2?family=${googleFont}&display=swap`;
            if (existingFontLink?.getAttribute('href') !== href) {
                const link = existingFontLink || document.createElement('link');
                link.id = fontLinkId;
                link.rel = 'stylesheet';
                link.href = href;
                if (!existingFontLink) document.head.appendChild(link);
            }
        } else if (existingFontLink) {
            existingFontLink.remove();
        }

        // Body styles are set explicitly as well: this prevents a page-level
        // body rule from overriding the appearance selected in System Settings.
        document.body.style.fontFamily = fontFamily;
        document.body.style.fontSize = '1rem';

        // Save to local storage
        localStorage.setItem('app-theme', JSON.stringify(theme));
    }, [theme]);

    const updateTheme = (key, value) => {
        setTheme(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const saveThemeToDB = async ({ silent = false } = {}) => {
        try {
            const { systemService } = await import('../services/systemService');
            const data = await systemService.getInstitutionDetails();
            const result = await systemService.updateInstitutionDetails(data?.id || null, {
                theme_primary: theme.primary,
                theme_secondary: theme.secondary,
                theme_accent: theme.accent,
                theme_font_family: theme.fontFamily,
                theme_font_size: theme.fontSize
            });
            if (!result.success) throw result.error || new Error(result.message);
            if (result.data) updateInstitutionSettings(result.data);
            if (!silent) alert('Theme saved successfully.');
            return result.data;
        } catch (error) {
            console.error('Unable to save theme:', error);
            if (!silent) alert(`Unable to save theme: ${error?.message || 'Unknown database error'}`);
            return null;
        }
    };

    const resetTheme = () => {
        setTheme(defaultTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, updateTheme, resetTheme, saveThemeToDB, institutionSettings, updateInstitutionSettings, refreshInstitutionSettings }}>
            {children}
        </ThemeContext.Provider>
    );
};
